import React, { useImperativeHandle, useMemo, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAnswersMutation } from '../../features/quizApiSlice';
import { validateSlide } from './utils/quizValidation';
import { handleUnselectAll, removeNoneSelection } from './utils/multiSelectHandler';
import { handleDietNavigation } from './utils/dietNavigationHandler';
import { transformQuizDataForSubmission } from './utils/quizDataTransformer';
import {
  FEMALE_GENDER_VALUE,
  GENDER_SLIDE_CODE,
  getOptionalButtonActivatedSlides,
  getFocusAreaActivatedSlides,
  MENOPAUSE_FOCUS_AREA_NAME,
  MULTI_SELECT_SLIDE_CODES,
} from './config/quizConfig';
import { COUNTRY_OPTIONS } from '../../utils';
import { PILL_INFO } from './config/pillInfo';
import { CONDITIONAL_INSIGHTS, getInsightForSlide } from './config/conditionalInsights';
import { CONDITIONS_SLIDE_CODE, MEDICATIONS_SLIDE_CODE, MEDICAL_REASSURANCE } from './config/reassurance';
import DobSelect from './Components/DobSelect';
import WeightHeightInputs from './Components/WeightHeightInputs';
import QuizSelect from './Components/QuizSelect';
import ThankYouCustomQuiz from './Components/ThankYouCustomQuiz';
import ThankYouDefault from './Components/ThankYouDefault';
import { NORMALIZED_REASSURANCE, normalizeKey } from './utils/quizHelpers';
import InfoPill from './Components/InfoPill';
import { normalizeLanguageCode } from '../../utils/language';
import OptionCard from './Components/OptionCard';
import ReassuranceBadge from './Components/ReassuranceBadge';
import GdprCheckbox from './Components/GdprCheckbox';
import InsightCard from './Components/InsightCard';
import QuizIntroSlideContent from './Components/QuizIntroSlideContent';
import QuizEmailSlideContent from './Components/QuizEmailSlideContent';
import FocusPrioritySlide from './Components/FocusPrioritySlide';
import { getClassmedResultPath } from '../../utils/classmed';
import {
  FOCUS_AREAS_SLIDE_CODE,
  FOCUS_PRIORITY_FIELD,
  FOCUS_PRIORITY_SLIDE_CODE,
  buildAutoFocusPriority,
  getFocusAreaOptionsFromQuizData,
  getFocusAreaOptionsFromSlide,
  getFocusPriorityValue,
  getSelectedFocusAreas,
  normalizeFocusPriority,
} from './utils/focusPriority';

const prioritizeMenopauseOption = (options) => [
  ...options.filter((option) => option?.name === MENOPAUSE_FOCUS_AREA_NAME),
  ...options.filter((option) => option?.name !== MENOPAUSE_FOCUS_AREA_NAME),
];

const placeOptionAfter = (options, optionName, afterName) => {
  const option = options.find((item) => item?.name === optionName);
  if (!option) return options;

  const withoutOption = options.filter((item) => item?.name !== optionName);
  const afterIndex = withoutOption.findIndex((item) => item?.name === afterName);
  if (afterIndex < 0) return withoutOption.concat(option);

  return [
    ...withoutOption.slice(0, afterIndex + 1),
    option,
    ...withoutOption.slice(afterIndex + 1),
  ];
};

const QuizSlide = React.forwardRef(
  (
    {
      slide,
      quizData,
      handleNext,
      slideValues,
      updateSlideData,
      updateSlideNavigation,
      slideNavigation,
      isLastSlide,
      lang,
      canPrefetchResults,
      onQuizComplete,
      isClassmedMode = false,
      classmedId = '',
      classmedPharmacyId = '',
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const offer = searchParams.get('offer');

    const { t, i18n } = useTranslation();
    const [submitAnswers] = useAnswersMutation();
    const [prefetchAnswers] = useAnswersMutation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const prefetchPromiseRef = useRef(null);
    const prefetchInvalidRef = useRef(false);
    const slideCode = slide[0]?.code;
    const currentLang = normalizeLanguageCode(lang || localStorage.getItem('lang') || i18n.language);
    const canShowMenopauseFocusArea = slideValues?.[GENDER_SLIDE_CODE]?.gender?.value === FEMALE_GENDER_VALUE;
    const shouldShowOption = (option) =>
      option?.name !== MENOPAUSE_FOCUS_AREA_NAME || canShowMenopauseFocusArea;

    // Pre-fetch when user reaches the last slide (background — never blocks button)
    useEffect(() => {
      if (!isLastSlide) {
        prefetchInvalidRef.current = true; // user went back — invalidate
        return;
      }
      if (!canPrefetchResults) return;
      if (prefetchInvalidRef.current) {
        prefetchInvalidRef.current = false;
        prefetchPromiseRef.current = null; // reset so we re-fetch with new answers
      }
      if (prefetchPromiseRef.current) return; // already started
      if (location.pathname.includes('/custom-quiz')) return; // skip for custom quiz
      if (isClassmedMode) return;

      const transformedAnswers = transformQuizDataForSubmission(slideValues);
      const promise = prefetchAnswers({
        quiz_id: 0,
        answers: transformedAnswers,
        lang: currentLang,
        dataJson: slideValues,
        slideNavigation,
        offer: offer && offer === '3' ? 3 : 1,
        includeAuth: false,
      });
      prefetchPromiseRef.current = promise;
      window.__quizPrefetchPromise = promise;
    }, [canPrefetchResults, isClassmedMode, isLastSlide, location.pathname, offer, prefetchAnswers, slideNavigation, slideValues, currentLang]);

    const getReassuranceText = (conditionName) => {
      if (!conditionName) return null;
      const entry = MEDICAL_REASSURANCE[conditionName] || NORMALIZED_REASSURANCE[normalizeKey(conditionName)];
      if (!entry) return null;
      return t(entry.textKey);
    };

    const getFocusImage = (comp) => {
      if (slideCode !== 11 || !comp?.img) return null;
      return (
        <img
          src={`${process.env.REACT_APP_PUBLIC_URL}${comp.img}`}
          alt=""
          className="t-w-5 t-h-5 t-object-contain"
        />
      );
    };

    const handleValueChange = (component, key, value) => {
      const slideEntry = slideValues[slideCode] || {};

      // State-based multi-select helpers so \"None\" works with the new card UI
      if (component.action === 'unselect-all') {
        // When selecting \"None\", clear all other selected options on this slide
        Object.entries(slideEntry).forEach(([field, data]) => {
          if (field !== key && data?.value) {
            updateSlideData(slideCode, field, false, data.answerCode);
          }
        });
        // Keep legacy DOM helper for backwards compatibility
        handleUnselectAll(slideCode, key, updateSlideData, component.group);
      } else {
        // When selecting any non-None option, clear a previously selected \"None\" if present
        const noneField = Object.keys(slideEntry).find((field) => field.toLowerCase().includes('none'));
        if (noneField && slideEntry[noneField]?.value) {
          const data = slideEntry[noneField];
          updateSlideData(slideCode, noneField, false, data?.answerCode);
        }
        // Legacy DOM-based cleanup (for older image-checkbox markup)
        removeNoneSelection(slideCode, updateSlideData, component.group);
      }

      updateSlideData(slideCode, key, value, component.answerCode);
    };

    const performNext = (component, isChecked = true, opts = {}) => {
      // If this is a choice-style next (button/img-button with name+value) that
      // has an insight configured, do not auto-advance on click unless explicitly allowed.
      if (
        component.name &&
        component.value != null &&
        CONDITIONAL_INSIGHTS[`${slideCode}::${component.value}`] &&
        !opts.allowInsightAdvance
      ) {
        return;
      }

      const validation = validateSlide(slideCode, slideValues, quizData);
      if (!validation.valid) {
        if (validation.message) toast.error(validation.message);
        return;
      }

      let activatedSlideCodes = [];
      if (
        component.type !== 'checkbox' &&
        component.type !== 'img-checkbox' &&
        component.name &&
        component.value != null &&
        String(component.value) !== ''
      ) {
        const slideCodes = getOptionalButtonActivatedSlides(slideCode, component.name, component.value);
        const validSlideCodes = slideCodes.filter((code) => code > 0);
        if (validSlideCodes.length > 0) {
          updateSlideNavigation(validSlideCodes, true);
          activatedSlideCodes = validSlideCodes;
        }
      }

      const { deactivated: deactivatedFromDiet, activated: activatedFromDiet } = handleDietNavigation(
        slideCode,
        slideValues,
        updateSlideNavigation,
        updateSlideData,
      );
      const delayMs = Math.max(0, Number(opts.delayMs) || 0);
      const doNext = () => handleNext([...activatedSlideCodes, ...activatedFromDiet], deactivatedFromDiet);
      if (delayMs > 0) {
        setTimeout(doNext, delayMs);
      } else {
        doNext();
      }
    };

    const handleClick = (e, component, isChecked = true, opts = {}) => {
      if (
        component.name &&
        component.value != null &&
        String(component.value) !== '' &&
        (component.type === 'button' || component.type === 'img-button')
      ) {
        if (slideCode === 12 && component.name === 'agree') {
          const consentAnswerCode = slideValues?.[1]?.agree?.answerCode ?? component.answerCode ?? 2;
          updateSlideData(1, 'agree', true, consentAnswerCode);
        } else {
          updateSlideData(slideCode, component.name, component.value, component.answerCode);
        }
      }

      if ((component.type === 'checkbox' || component.type === 'img-checkbox') && component.name) {
        const slideCodes = slideCode === FOCUS_AREAS_SLIDE_CODE ? getFocusAreaActivatedSlides(component.name) : [];
        const validSlideCodes = slideCodes.filter((code) => code > 0);
        if (validSlideCodes.length > 0) {
          updateSlideNavigation(validSlideCodes, isChecked);
        }

        if (slideCode === FOCUS_AREAS_SLIDE_CODE) {
          const focusAreaOptions = getFocusAreaOptionsFromSlide(slide).filter(shouldShowOption);
          const focusAreaValues = slideValues[FOCUS_AREAS_SLIDE_CODE] || {};
          const selectedFocusAreas = focusAreaOptions.filter((option) => {
            if (option.name === component.name) return isChecked;
            return !!focusAreaValues?.[option.name]?.value;
          });

          updateSlideNavigation([FOCUS_PRIORITY_SLIDE_CODE], selectedFocusAreas.length > 1);

          if (selectedFocusAreas.length === 1) {
            updateSlideData(
              FOCUS_PRIORITY_SLIDE_CODE,
              FOCUS_PRIORITY_FIELD,
              buildAutoFocusPriority(selectedFocusAreas),
              null,
            );
          } else if (selectedFocusAreas.length > 1) {
            updateSlideData(
              FOCUS_PRIORITY_SLIDE_CODE,
              FOCUS_PRIORITY_FIELD,
              normalizeFocusPriority(getFocusPriorityValue(slideValues), selectedFocusAreas),
              null,
            );
          } else {
            updateSlideData(FOCUS_PRIORITY_SLIDE_CODE, FOCUS_PRIORITY_FIELD, false, null);
          }
        }
      }

      if (component.action === 'next') {
        if (slideCode === 12) {
          const consentAnswerCode = slideValues?.[1]?.agree?.answerCode ?? 2;
          updateSlideData(1, 'agree', true, consentAnswerCode);
        }
        const shouldDelayAutoAdvance =
          !opts.allowInsightAdvance &&
          (component.type === 'button' || component.type === 'img-button') &&
          component.name &&
          component.value != null &&
          String(component.value) !== '';

        performNext(component, isChecked, {
          ...opts,
          delayMs: shouldDelayAutoAdvance ? 200 : opts.delayMs,
        });
      } else if (component.action === 'summary') {
        submitQuizAnswers();
      }
    };

    const triggerContinueAction = ({ forceConsent = false } = {}) => {
      if (forceConsent && slideCode === 12) {
        const consentAnswerCode = slideValues?.[1]?.agree?.answerCode ?? 2;
        updateSlideData(1, 'agree', true, consentAnswerCode);
      }
      // Some slides keep "next" actions inside group.controls (e.g. img-button grids).
      // Flatten all possible next actions first so the footer works everywhere.
      const nextActions = [];
      slide.forEach((c) => {
        if (!c) return;
        if (c.action === 'next') nextActions.push(c);
        if (c.type === 'group' && Array.isArray(c.controls)) {
          c.controls.forEach((ctrl) => {
            if (ctrl?.action === 'next') nextActions.push(ctrl);
          });
        }
      });

      const choiceNexts = nextActions.filter((c) => c.name && c.value != null && String(c.value) !== '');
      const genericNext = nextActions.find((c) => !c.name || c.value == null || String(c.value) === '');

      // If the slide uses choice-style "next" options:
      // - Without insight: do nothing (auto-advance already handled on option click)
      // - With insight: advance using the currently selected choice.
      if (choiceNexts.length > 0) {
        // If user is navigating back and already has a selected answer,
        // allow continuing forward without requiring another click.
        const selectedComp =
          choiceNexts.find((c) => {
            const saved = slideValues[slideCode]?.[c.name]?.value;
            return saved != null && String(saved) === String(c.value);
          }) || null;

        if (!insight) {
          if (selectedComp) {
            performNext(selectedComp, true);
          }
          return;
        }

        // Some slides have a dedicated generic "Next" action
        // that should be triggered by the footer, even though choice-style next options exist.
        if (genericNext) {
          performNext(genericNext, true, { allowInsightAdvance: true });
          return;
        }

        const fallbackComp = selectedComp || choiceNexts[0];
        if (fallbackComp) {
          performNext(fallbackComp, true, { allowInsightAdvance: true });
        }
        return;
      }

      const nextComp = genericNext || nextActions[0];
      if (nextComp) {
        handleClick({ preventDefault: () => {} }, nextComp);
      }
    };

    // Expose handleContinue for the sticky footer CTA
    useImperativeHandle(ref, () => ({
      handlePrevious() {},
      handleContinue() {
        triggerContinueAction({ forceConsent: slideCode === 12 });
      },
    }));

    const submitQuizAnswers = async () => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      await onQuizComplete?.();

      if (location.pathname.includes('/custom-quiz')) {
        const promise = prefetchPromiseRef.current || submitAnswers({
          quiz_id: 0,
          answers: transformQuizDataForSubmission(slideValues),
          lang: currentLang,
          dataJson: slideValues,
          slideNavigation,
          offer: offer && offer === '3' ? 3 : 1,
        });
        promise
          .then((res) => navigate('/custom-quiz-success'))
          .catch((error) => { console.error('Error submitting quiz:', error); setIsSubmitting(false); });
        return;
      }

      if (isClassmedMode) {
        navigate(getClassmedResultPath(classmedId, { pharmacyId: classmedPharmacyId }), {
          state: {
            quizAnswers: transformQuizDataForSubmission(slideValues),
            slideValues,
            slideNavigation,
            offer: offer && offer === '3' ? 3 : 1,
            classmedId,
            classmedPharmacyId,
            freshFromQuiz: true,
          },
        });
        return;
      }

      const productSuggestionPath = location.search
        ? `/product-suggestion${location.search}`
        : '/product-suggestion';

      // Navigate immediately — prefetch stores quiz_id in localStorage when it resolves
      navigate(productSuggestionPath, {
        state: {
          quizAnswers: transformQuizDataForSubmission(slideValues),
          slideValues,
          slideNavigation,
          offer: offer && offer === '3' ? 3 : 1,
          freshFromQuiz: true,
        },
      });
    };

    const insight = getInsightForSlide(slideCode, slideValues);
    const {
      pill,
      isMultiSlide,
      isConditionsSlide,
      isDobSlide,
      isWeightHeightSlide,
      titleComp,
      labelComps,
      textComps,
      selectComps,
      checkboxComps,
      buttonComps,
      imgButtonComps,
      imgCheckboxComps,
      groupComps,
      groupedOptions,
      groupedTextInputs,
      groupedLabels,
      hasStandaloneButtons,
    } = useMemo(() => {
      const nextTitleComp = slide.find((c) => c.type === 'title');
      const nextLabelComps = slide.filter((c) => c.type === 'label');
      const nextTextComps = slide.filter((c) => c.type === 'text' && !c.controls);
      const nextSelectComps = slide.filter((c) => c.type === 'select');
      const nextCheckboxComps = slide.filter((c) => c.type === 'checkbox');
      const nextButtonComps = slide.filter((c) => {
        if (c.type !== 'button' || c.action === 'summary') return false;
        if (c.action === 'next' && c.name && c.value != null && String(c.value) !== '') return true;
        if (c.action !== 'next') return true;
        return false;
      });
      const nextImgButtonComps = slide.filter((c) => c.type === 'img-button');
      const nextImgCheckboxComps = slide.filter((c) => c.type === 'img-checkbox');
      const nextGroupComps = slide.filter((c) => c.type === 'group');

      const nextGroupedOptions = [];
      const nextGroupedTextInputs = [];
      const nextGroupedLabels = [];

      nextGroupComps.forEach((group) => {
        if (!group.controls) return;
        group.controls.forEach((ctrl) => {
          if (ctrl.type === 'img-button' || ctrl.type === 'img-checkbox' || ctrl.type === 'img-radio') {
            nextGroupedOptions.push({ ...ctrl, _groupName: group.name });
          }
          if (ctrl.type === 'text') nextGroupedTextInputs.push(ctrl);
          if (ctrl.type === 'label') nextGroupedLabels.push(ctrl);
        });
      });

      return {
        pill: PILL_INFO[slideCode],
        isMultiSlide: MULTI_SELECT_SLIDE_CODES.has(slideCode),
        isConditionsSlide: slideCode === CONDITIONS_SLIDE_CODE || slideCode === MEDICATIONS_SLIDE_CODE,
        isDobSlide: slideCode === 7,
        isWeightHeightSlide: slideCode === 8,
        titleComp: nextTitleComp,
        labelComps: nextLabelComps,
        textComps: nextTextComps,
        selectComps: nextSelectComps,
        checkboxComps: nextCheckboxComps,
        buttonComps: nextButtonComps,
        imgButtonComps: nextImgButtonComps,
        imgCheckboxComps: nextImgCheckboxComps,
        groupComps: nextGroupComps,
        groupedOptions: nextGroupedOptions,
        groupedTextInputs: nextGroupedTextInputs,
        groupedLabels: nextGroupedLabels,
        hasStandaloneButtons: nextButtonComps.length > 0 || nextImgButtonComps.length > 0,
      };
    }, [slide, slideCode]);

    // Keep the female-only menopause option first without changing the remaining option order.
    const getVisibleGroupedOptions = () => {
      if (slideCode === FOCUS_AREAS_SLIDE_CODE) {
        return prioritizeMenopauseOption(groupedOptions.filter(shouldShowOption));
      }

      if (slideCode === MEDICATIONS_SLIDE_CODE) {
        return placeOptionAfter(groupedOptions, 'Contraceptive-spiral', 'Birth-control-pill');
      }

      return groupedOptions;
    };

    const visibleGroupedOptions = getVisibleGroupedOptions();
    const visibleImgCheckboxComps =
      slideCode === FOCUS_AREAS_SLIDE_CODE
        ? prioritizeMenopauseOption(imgCheckboxComps.filter(shouldShowOption))
        : imgCheckboxComps;
    const hasVisibleGroupedOptions = visibleGroupedOptions.length > 0;
    const hasVisibleImgCheckboxes = visibleImgCheckboxComps.length > 0;

    if (isLastSlide && location.pathname.includes('/custom-quiz')) {
      return (
        <ThankYouCustomQuiz
          title={t('Quiz_CustomFinal_Title')}
          buttonLabel={t('Quiz_CustomFinal_Button')}
          onSubmit={submitQuizAnswers}
          isLoading={isSubmitting}
        />
      );
    }

    if (isLastSlide) {
      const userName = slideValues[1]?.name?.value || '';
      return <ThankYouDefault t={t} userName={userName} onSubmit={submitQuizAnswers} isLoading={isSubmitting} />;
    }

    if (slideCode === 1) {
      const nameField = textComps.find((comp) => comp.name === 'name') || textComps[0];
      return (
        <QuizIntroSlideContent
          t={t}
          nameField={nameField}
          slideValues={slideValues}
          slideCode={slideCode}
          handleValueChange={handleValueChange}
        />
      );
    }

    if (slideCode === 12) {
      const emailField = textComps.find((comp) => comp.name === 'email') || textComps[0];
      return (
        <>
          <QuizEmailSlideContent
            t={t}
            emailField={emailField}
            slideValues={slideValues}
            slideCode={slideCode}
            handleValueChange={handleValueChange}
          />
        </>
      );
    }

    if (slideCode === FOCUS_PRIORITY_SLIDE_CODE) {
      const focusAreaOptions = getFocusAreaOptionsFromQuizData(quizData).filter(shouldShowOption);
      const selectedFocusAreas = getSelectedFocusAreas(slideValues, focusAreaOptions);
      const priorityValue = getFocusPriorityValue(slideValues);
      const subtitle = labelComps[0]?.label || groupedLabels[0]?.label || '';

      return (
        <FocusPrioritySlide
          title={titleComp?.title || ''}
          subtitle={subtitle}
          selectedFocusAreas={selectedFocusAreas}
          priorityValue={priorityValue}
          onChange={(value) => {
            updateSlideData(
              FOCUS_PRIORITY_SLIDE_CODE,
              FOCUS_PRIORITY_FIELD,
              normalizeFocusPriority(value, selectedFocusAreas),
              null,
            );
          }}
        />
      );
    }

    // ─── Regular Slide ───

    return (
      <div>
        {titleComp && (
          <h2
            className="t-font-serif t-text-[28px] md:t-text-3xl t-leading-tight t-text-navy t-mb-[18px] t-m-0"
            dangerouslySetInnerHTML={{ __html: titleComp.title }}
          />
        )}

        {pill && (
          <InfoPill
            type={pill.type}
            text={t(pill.textKey)}
            whyLabel={t('Quiz_Info_WhyWeAsk')}
            dykLabel={t('Quiz_Info_DidYouKnow')}
          />
        )}

        {!isDobSlide && !isWeightHeightSlide && (
          <>
            {labelComps.map((comp) => (
              <p
                key={`label-${comp.answerCode}`}
                className="t-text-[13px] t-leading-relaxed t-text-g500 t-mb-3"
              >
                <span dangerouslySetInnerHTML={{ __html: comp.label }} />
              </p>
            ))}
            {groupedLabels.map((comp) => (
              <p
                key={`glabel-${comp.answerCode}`}
                className="t-text-[13px] t-leading-relaxed t-text-g500 t-mb-3"
              >
                <span dangerouslySetInnerHTML={{ __html: comp.label }} />
              </p>
            ))}
          </>
        )}

        {(isMultiSlide ||
          hasVisibleImgCheckboxes ||
          (hasVisibleGroupedOptions && visibleGroupedOptions[0]?.type === 'img-checkbox')) && (
          <p className="t-text-xs t-text-g500 t-mb-2.5 t-font-medium">
            {t('Quiz_SelectAllThatApply')}
          </p>
        )}

        {!isDobSlide &&
          textComps.map((comp) => (
            <input
              key={`text-${slideCode}-${comp.name}`}
              type={comp.name === 'email' ? 'email' : 'text'}
              id={comp.name}
              placeholder={comp.placeholder}
              value={slideValues[slideCode]?.[comp.name]?.value ?? ''}
              onChange={(e) => handleValueChange(comp, comp.name, e.target.value)}
              className="t-w-full t-py-3.5 t-px-4 t-border-2 t-border-g200 t-rounded-xl t-font-sans t-text-base t-text-navy t-bg-white t-outline-none focus:t-border-quiz-teal placeholder:t-text-g300 t-mb-3"
              autoFocus={textComps.length === 1}
            />
          ))}

        {isDobSlide && (
          <DobSelect
            slideCode={slideCode}
            slideValues={slideValues}
            groupComps={groupComps}
            handleValueChange={handleValueChange}
            currentLang={currentLang}
          />
        )}

        {checkboxComps.map((comp) => (
          <GdprCheckbox
            key={`chk-${comp.name}`}
            id={comp.name}
            checked={!!slideValues[slideCode]?.[comp.name]?.value}
            onChange={(val) => handleValueChange(comp, comp.name, val)}
            label={comp.label}
          />
        ))}

        {!isDobSlide &&
          groupedTextInputs.length > 0 &&
          (isWeightHeightSlide ? (
            <WeightHeightInputs
              slideCode={slideCode}
              slideValues={slideValues}
              groupedTextInputs={groupedTextInputs}
              groupComps={groupComps}
              handleValueChange={handleValueChange}
              currentLang={currentLang}
            />
          ) : (
            <div className="t-flex t-gap-2.5">
              {groupedTextInputs.map((ctrl) => (
                <input
                  key={`gtext-${ctrl.name}`}
                  type="number"
                  id={ctrl.name}
                  placeholder={ctrl.placeholder}
                  value={slideValues[slideCode]?.[ctrl.name]?.value ?? ''}
                  onChange={(e) => handleValueChange(ctrl, ctrl.name, e.target.value)}
                  className="t-flex-1 t-py-3.5 t-px-4 t-border-2 t-border-g200 t-rounded-xl t-font-sans t-text-base t-text-navy t-bg-white t-outline-none focus:t-border-quiz-teal placeholder:t-text-g300"
                />
              ))}
            </div>
          ))}

        {selectComps.map((comp) => {
          let options = [];
          if (comp.name === 'country') {
            options = COUNTRY_OPTIONS.map((c) => ({
              value: c.value,
              label: t(c.labelKey),
            }));
          } else if (typeof comp.value === 'string') {
            options = comp.value.split(',').map((o) => {
              const cleaned = o.replace(/"/g, '').trim();
              return { value: cleaned, label: cleaned };
            });
          }
          return (
            <QuizSelect
              key={`quiz-select-${slideCode}-${comp.name}`}
              component={comp}
              slideCode={slideCode}
              value={slideValues[slideCode]?.[comp.name]?.value}
              options={options}
              onChange={(val) => handleValueChange(comp, comp.name, val)}
            />
          );
        })}

        {hasStandaloneButtons && (
          <div className="t-flex t-flex-col t-gap-2">
            {[...buttonComps, ...imgButtonComps].map((comp) => (
              <OptionCard
                key={`btn-${comp.answerCode}`}
                label={comp.label}
                icon={getFocusImage(comp)}
                isSelected={slideValues[slideCode]?.[comp.name]?.value === comp.value}
                isMulti={false}
                onClick={(e) => {
                  e.preventDefault();
                  handleClick(e, comp);
                }}
              />
            ))}
          </div>
        )}

        {hasVisibleImgCheckboxes && (
          <div className="t-flex t-flex-col t-gap-2">
            {visibleImgCheckboxComps.map((comp) => {
              const isChecked = !!slideValues[slideCode]?.[comp.name]?.value;
              return (
                <div key={`imgchk-${comp.answerCode}`}>
                  <OptionCard
                    label={comp.label}
                    icon={getFocusImage(comp)}
                    isSelected={isChecked}
                    isMulti={true}
                    onClick={() => {
                      const newChecked = !isChecked;
                      handleValueChange(comp, comp.name, newChecked || false);
                      handleClick(null, comp, newChecked);
                    }}
                  />
                  {isChecked && isConditionsSlide && getReassuranceText(comp.name) && (
                    <ReassuranceBadge text={getReassuranceText(comp.name)} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {hasVisibleGroupedOptions && (
          <div className="t-flex t-flex-col t-gap-2" id={groupComps[0]?.name}>
            {visibleGroupedOptions.map((ctrl) => {
              const isMulti = ctrl.type === 'img-checkbox';
              const isRadio = ctrl.type === 'img-radio';
              const isChecked = isMulti
                ? !!slideValues[slideCode]?.[ctrl.name]?.value
                : slideValues[slideCode]?.[ctrl.name]?.value === ctrl.value;

              return (
                <div key={`gopt-${ctrl.answerCode}`}>
                  <OptionCard
                    label={ctrl.label}
                    icon={getFocusImage(ctrl)}
                    isSelected={isChecked}
                    isMulti={isMulti}
                    onClick={(e) => {
                      if (isMulti) {
                        const newChecked = !isChecked;
                        handleValueChange(ctrl, ctrl.name, newChecked || false);
                        handleClick(e, ctrl, newChecked);
                      } else if (isRadio) {
                        handleValueChange(ctrl, ctrl.name, ctrl.value);
                        handleClick(e, ctrl, true);
                      } else {
                        e.preventDefault();
                        handleClick(e, ctrl);
                      }
                    }}
                  />
                  {isChecked && isConditionsSlide && getReassuranceText(ctrl.name) && (
                    <ReassuranceBadge text={getReassuranceText(ctrl.name)} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {insight && (
          <InsightCard
            title={t(insight.titleKey)}
            text={t(insight.textKey)}
          />
        )}
      </div>
    );
  },
);

export default QuizSlide;
